import { useState, type ReactNode } from 'react';
import { CaretDownNew } from '@hungryroot/icons';
import { Price, Highlight } from '@hungryroot/pho';
import { Paragraph, Heading, Dimension, Sheet } from '@hungryroot/ramen';
import styles from './OrderSummaryFooterOverlay.module.css';

interface Props {
  amountSummary: ReactNode;
  continueButton: ReactNode;
  discount?: number;
  summaryText?: string;
  children: ReactNode;
  subtotal: number;
  discountLabel?: string;
}

export function OrderSummaryFooterOverlay({
  continueButton,
  amountSummary,
  discount,
  summaryText,
  children,
  subtotal,
  discountLabel,
}: Props) {
  const [isSheetShown, setIsSheetShown] = useState(false);

  if (isSheetShown) {
    return (
      <Sheet
        initialFocusRef={undefined}
        ariaLabel="summary"
        testid="footer-overlay"
        active={true}
        showTouchBar={true}
        onRequestDismiss={() => {
          setIsSheetShown(false);
        }}
      >
        {children}
      </Sheet>
    );
  }

  return (
    <Dimension level="two" data-testid="footer-overlay">
      <div className={styles['summary-container']}>
        <div className={styles.row}>
          <div>
            {discount !== undefined && discount > 0 && (
              <div className={styles.discount}>
                <Paragraph variant="h7">
                  <Highlight className={styles.highlight}>
                    {discountLabel}
                  </Highlight>{' '}
                </Paragraph>
                <Paragraph variant="caption">
                  <Price amount={subtotal} className={styles.subtotal} />
                </Paragraph>
              </div>
            )}
            <div className={styles.total}>
              <Heading variant="H3">{summaryText}</Heading>
              {amountSummary}
            </div>
          </div>
          <button
            data-testid="total-expand-button"
            onClick={() => setIsSheetShown(true)}
            aria-label="expand total summary"
            className={styles.expand}
          >
            <CaretDownNew />
          </button>
        </div>
        {continueButton}
      </div>
    </Dimension>
  );
}
